import { api } from "./client";
import type {
  CartResponse,
  CatalogProduct,
  CatalogResponse,
  LoginResponse,
  Order,
  OrdersResponse,
  TermsInfo,
  User,
} from "./types";

// ---------- public applications ----------

export interface ApplicationPayload {
  company_name: string;
  contact_name: string;
  email: string;
  phone?: string;
  desired_plan: "light" | "standard" | "premium";
  business_description?: string;
  shipping_postal_code?: string;
  shipping_address?: string;
}

export async function submitApplication(
  payload: ApplicationPayload,
): Promise<{ application_id: number; message: string }> {
  // 未ログインでも叩ける。axios インスタンスは共通だが token が無ければ付かない。
  const resp = await api.post<{ application_id: number; message: string }>(
    "/applications",
    payload,
  );
  return resp.data;
}

// ---------- auth ----------

export async function login(email: string, password: string): Promise<LoginResponse> {
  const resp = await api.post<LoginResponse>("/auth/login", { email, password });
  return resp.data;
}

export async function logout(): Promise<void> {
  await api.post("/auth/logout");
}

export async function me(): Promise<User> {
  const resp = await api.get<{ user: User }>("/auth/me");
  return resp.data.user;
}

export async function fetchTerms(): Promise<TermsInfo> {
  const resp = await api.get<TermsInfo>("/auth/terms");
  return resp.data;
}

export async function acceptTerms(version: string): Promise<void> {
  await api.post("/auth/terms/accept", { version });
}

// ---------- account cancellation ----------

export interface CancellationStatus {
  cancellation_requested_at: string | null;
  cancellation_reason: string | null;
  cancellation_finalized_at: string | null;
}

export async function fetchCancellationStatus(): Promise<CancellationStatus> {
  const resp = await api.get<CancellationStatus>("/account/cancellation-request");
  return resp.data;
}

export async function requestCancellation(
  reason?: string,
): Promise<CancellationStatus> {
  const resp = await api.post<CancellationStatus>("/account/cancellation-request", {
    reason: reason || null,
  });
  return resp.data;
}

export async function withdrawCancellation(): Promise<CancellationStatus> {
  const resp = await api.delete<CancellationStatus>("/account/cancellation-request");
  return resp.data;
}

export interface PlanChangeResult {
  plan: "light" | "standard" | "premium";
  previous_plan: "light" | "standard" | "premium";
  change_type: "upgrade" | "downgrade";
  accessible_tiers: number[];
}

export async function changePlan(
  plan: "light" | "standard" | "premium",
): Promise<PlanChangeResult> {
  const resp = await api.put<PlanChangeResult>("/account/plan", { plan });
  return resp.data;
}

export interface PlanChangePreview {
  plan: "light" | "standard" | "premium";
  current_plan: "light" | "standard" | "premium";
  change_type: "upgrade" | "downgrade";
  amount_due: number | null;
  currency: string;
  is_trial: boolean;
  note?: string;
}

export async function previewPlanChange(
  plan: "light" | "standard" | "premium",
): Promise<PlanChangePreview> {
  const resp = await api.get<PlanChangePreview>("/account/plan-change-preview", {
    params: { plan },
  });
  return resp.data;
}

export async function cancelPendingPlanChange(): Promise<{
  plan: string;
  pending_plan_change: null;
}> {
  const resp = await api.delete<{ plan: string; pending_plan_change: null }>(
    "/account/pending-plan-change",
  );
  return resp.data;
}

// ---------- subscription / billing portal ----------

export interface SubscriptionStatus {
  plan: "light" | "standard" | "premium";
  payment_status: "active" | "past_due" | "cancelled";
  stripe_subscription_id: string | null;
  stripe_customer_id: string | null;
  status: string | null; // trialing / active / past_due / canceled / ...
  current_period_end: number | null;
  trial_end: number | null;
}

export async function fetchSubscription(): Promise<SubscriptionStatus> {
  const resp = await api.get<SubscriptionStatus>("/account/subscription");
  return resp.data;
}

export async function createBillingPortalSession(
  returnUrl: string,
): Promise<{ url: string }> {
  const resp = await api.post<{ url: string }>(
    "/account/billing-portal-session",
    { return_url: returnUrl },
  );
  return resp.data;
}

export async function changePassword(current: string, next: string): Promise<void> {
  await api.put("/auth/password", {
    current_password: current,
    new_password: next,
  });
}

// ---------- catalog ----------

export interface CatalogQuery {
  q?: string;
  category?: string;
  brand?: string;
  page?: number;
  per_page?: number;
  include_out_of_stock?: boolean;
}

export async function fetchCatalog(params: CatalogQuery = {}): Promise<CatalogResponse> {
  const resp = await api.get<CatalogResponse>("/catalog", { params });
  return resp.data;
}

export interface PopularResponse {
  products: CatalogProduct[];
  days: number;
  limit: number;
}

export async function fetchPopular(limit = 10, days = 30): Promise<PopularResponse> {
  const resp = await api.get<PopularResponse>("/catalog/popular", {
    params: { limit, days },
  });
  return resp.data;
}

export async function downloadCatalogCsv(includeOutOfStock = false): Promise<void> {
  const resp = await api.get("/catalog/export.csv", {
    params: { include_out_of_stock: includeOutOfStock || undefined },
    responseType: "blob",
  });
  const cd = resp.headers["content-disposition"] || "";
  const match = /filename="?([^";]+)"?/.exec(cd);
  const filename = match
    ? match[1]
    : `kantan-zaiko-products-${new Date().toISOString().slice(0, 10).replace(/-/g, "")}.csv`;
  const blob = resp.data as Blob;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// ---------- cart ----------

export async function fetchCart(): Promise<CartResponse> {
  const resp = await api.get<CartResponse>("/cart");
  return resp.data;
}

export async function addToCart(productId: number, quantity: number): Promise<CartResponse> {
  const resp = await api.post<CartResponse>("/cart/items", {
    product_id: productId,
    quantity,
  });
  return resp.data;
}

export async function updateCartItem(productId: number, quantity: number): Promise<CartResponse> {
  const resp = await api.put<CartResponse>(`/cart/items/${productId}`, { quantity });
  return resp.data;
}

export async function removeCartItem(productId: number): Promise<CartResponse> {
  const resp = await api.delete<CartResponse>(`/cart/items/${productId}`);
  return resp.data;
}

export async function clearCart(): Promise<CartResponse> {
  const resp = await api.delete<CartResponse>("/cart");
  return resp.data;
}

// ---------- cart CSV import ----------

export interface CartCsvImportError {
  row: number;
  jan: string;
  reason: string;
}

export interface CartCsvImportResult {
  added: number;
  updated: number;
  skipped: number;
  errors: CartCsvImportError[];
  cart: CartResponse;
}

export async function importCartCsv(file: File): Promise<CartCsvImportResult> {
  const form = new FormData();
  form.append("file", file);
  const resp = await api.post<CartCsvImportResult>("/cart/csv-import", form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return resp.data;
}

// ---------- payment methods ----------

export interface PaymentMethodInfo {
  registered: boolean;
  brand?: string | null;
  last4?: string | null;
  exp_month?: number | null;
  exp_year?: number | null;
  reason?: "no_stripe_customer" | "no_payment_method";
}

export async function fetchPaymentMethod(): Promise<PaymentMethodInfo> {
  const resp = await api.get<PaymentMethodInfo>("/payment-methods");
  return resp.data;
}

// ---------- orders ----------

export interface CreateOrderItem {
  product_id: number;
  quantity: number;
}

export interface CreateOrderParams {
  items: CreateOrderItem[];
  shipping_address?: string;
  // payment_method_id は送らない（バックエンドが既定カードに off-session 課金）
}

export async function createOrder(params: CreateOrderParams): Promise<Order> {
  const resp = await api.post<{ order: Order }>("/orders", params);
  return resp.data.order;
}

export async function fetchOrders(page = 1, per_page = 20): Promise<OrdersResponse> {
  const resp = await api.get<OrdersResponse>("/orders", { params: { page, per_page } });
  return resp.data;
}

export async function fetchOrder(id: number): Promise<Order> {
  const resp = await api.get<{ order: Order }>(`/orders/${id}`);
  return resp.data.order;
}
